import {motion, AnimatePresence} from 'motion/react';
import {useContext, useState, useRef} from 'react';
import {NotificationContext} from '../../providers/notification.provider';
import {ButtonComponent} from "../button/button.component";
import {ToastStyles} from "./toast.styles";
import {Variants} from "motion";
import {IconButtonComponent} from "../button/iconButton.component";

const wrapperVariants: Variants = {
    inactive: {
        transition: {
            staggerChildren: 0.005,
        },
        y: "10px",
        gap: "0px",
    },
    active: {
        transition: {
            staggerChildren: 0.005,
            delayChildren: 0.005,
        },
        y: "20px",
        gap: "10px"
    },
    complete: {},
};

const collapsibleVariants: Variants = {
    inactive: {
        y: "10px",
    },
    active: {
        y: "20px",
    },
};

const toastVariants: Variants = {
    inactive: ({index, length}: { index: number; length: number }) => {
        const step = 1 / length;
        const minStep = 0.1;
        const effectiveStep = Math.max(step, minStep);
        const scale = 1 - index * effectiveStep;
        const clampedScale = Math.max(Math.ceil(scale * 10) / 10, 0.4);

        return {
            scale: clampedScale,
            y: `${-40 * index}px`,
            transition: {
                duration: 0.1,
            },
            zIndex: length - index + 1,
        };
    },
    active: {
        scale: 1,
        y: 0,
        zIndex: 1
    },
    exit: {
        x: 300,
        opacity: 0,
        transition: {
            duration: 0.1
        }
    }
};

const useDragToRemove = (onRemove: () => void, threshold = 100) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState(0);
    const startX = useRef(0);

    const handleDragStart = (clientX: number) => {
        setIsDragging(true);
        startX.current = clientX;
        setDragOffset(0);
    };

    const handleDragMove = (clientX: number) => {
        if (!isDragging) return;

        const deltaX = clientX - startX.current;
        setDragOffset(deltaX);
    };

    const handleDragEnd = () => {
        if (!isDragging) return;

        setIsDragging(false);

        if (Math.abs(dragOffset) > threshold) {
            onRemove();
        } else {
            setDragOffset(0);
        }
    };

    const onTouchStart = (e: React.TouchEvent) => {
        handleDragStart(e.touches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        handleDragMove(e.touches[0].clientX);
    };

    const onTouchEnd = () => {
        handleDragEnd();
    };

    // Mouse handlers
    const onMouseDown = (e: React.MouseEvent) => {
        handleDragStart(e.clientX);
    };

    const onMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        handleDragMove(e.clientX);
    };

    const onMouseUp = () => {
        handleDragEnd();
    };

    const onMouseLeave = () => {
        if (isDragging) {
            handleDragEnd();
        }
    };

    return {
        isDragging,
        dragOffset,
        dragProps: {
            style: {
                transform: `translateX(${dragOffset}px)`,
                opacity: 1 - (Math.abs(dragOffset) / threshold) * 0.7,
                transition: isDragging ? 'none' : 'transform 0.3s ease, opacity 0.3s ease'
            },
            onTouchStart,
            onTouchMove,
            onTouchEnd,
            onMouseDown,
            onMouseMove,
            onMouseUp,
            onMouseLeave
        }
    };
};

export const ToastComponent = () => {
    const [status, setStatus] = useState<"inactive" | "active" | "complete">("inactive")
    const {notifications, dispatch} = useContext(NotificationContext)!;

    const toggle = () => {
        setStatus((prevState) => prevState === "inactive" ? "active" : "inactive")
    }

    const DraggableToast = ({notification, index}: { notification: any; index: number }) => {
        const {isDragging, dragOffset, dragProps} = useDragToRemove(() => {
            dispatch({
                type: "REMOVE_NOTIFICATION",
                payload: {id: notification.id}
            });
        }, 120);

        return (
            <motion.div
                key={notification.id}
                animate={status}
                initial={"inactive"}
                exit={"exit"}
                custom={{index: index, length: notifications.length}}
                variants={toastVariants}
                className={`rounded-2xl relative ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                id="toast-interactive"
                onClick={(e) => {
                    // Only toggle if not dragging
                    if (dragOffset === 0) {
                        toggle();
                    }
                }}
                role="alert"
                {...dragProps}
            >
                {/* Drag hint overlay */}
                {Math.abs(dragOffset) > 0 && (
                    <div
                        className="absolute inset-0 bg-red-500 bg-opacity-20 rounded-2xl flex items-center justify-end pr-4"
                        style={{
                            opacity: Math.min(Math.abs(dragOffset) / 80, 0.8)
                        }}
                    >
                        <svg
                            className="w-6 h-6 text-red-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                        <span className="text-red-500 text-sm font-medium ml-2">
                            {Math.abs(dragOffset) > 60 ? 'Release to remove' : 'Drag to remove'}
                        </span>
                    </div>
                )}

                <div className="flex relative z-10  rounded-2xl">
                    <div className="notification-icon">
                        {notification.status === 'error' ?
                            <svg style={{color: "white"}} viewBox="0 0 24 24" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M12 14.75C11.59 14.75 11.25 14.41 11.25 14V9C11.25 8.59 11.59 8.25 12 8.25C12.41 8.25 12.75 8.59 12.75 9V14C12.75 14.41 12.41 14.75 12 14.75Z"
                                    fill="currentColor"/>
                                <path
                                    d="M12 18.0001C11.94 18.0001 11.87 17.9901 11.8 17.9801C11.74 17.9701 11.68 17.9501 11.62 17.9201C11.56 17.9001 11.5 17.8701 11.44 17.8301C11.39 17.7901 11.34 17.7501 11.29 17.7101C11.11 17.5201 11 17.2601 11 17.0001C11 16.7401 11.11 16.4801 11.29 16.2901C11.34 16.2501 11.39 16.2101 11.44 16.1701C11.5 16.1301 11.56 16.1001 11.62 16.0801C11.68 16.0501 11.74 16.0301 11.8 16.0201C11.93 15.9901 12.07 15.9901 12.19 16.0201C12.26 16.0301 12.32 16.0501 12.38 16.0801C12.44 16.1001 12.5 16.1301 12.56 16.1701C12.61 16.2101 12.66 16.2501 12.71 16.2901C12.89 16.4801 13 16.7401 13 17.0001C13 17.2601 12.89 17.5201 12.71 17.7101C12.66 17.7501 12.61 17.7901 12.56 17.8301C12.5 17.8701 12.44 17.9001 12.38 17.9201C12.32 17.9501 12.26 17.9701 12.19 17.9801C12.13 17.9901 12.06 18.0001 12 18.0001Z"
                                    fill="currentColor"/>
                                <path
                                    d="M18.06 22.1601H5.93998C3.98998 22.1601 2.49998 21.4501 1.73998 20.1701C0.989976 18.8901 1.08998 17.2401 2.03998 15.5301L8.09998 4.63009C9.09998 2.83009 10.48 1.84009 12 1.84009C13.52 1.84009 14.9 2.83009 15.9 4.63009L21.96 15.5401C22.91 17.2501 23.02 18.8901 22.26 20.1801C21.5 21.4501 20.01 22.1601 18.06 22.1601ZM12 3.34009C11.06 3.34009 10.14 4.06009 9.40998 5.36009L3.35998 16.2701C2.67998 17.4901 2.56998 18.6101 3.03998 19.4201C3.50998 20.2301 4.54998 20.6701 5.94998 20.6701H18.07C19.47 20.6701 20.5 20.2301 20.98 19.4201C21.46 18.6101 21.34 17.5001 20.66 16.2701L14.59 5.36009C13.86 4.06009 12.94 3.34009 12 3.34009Z"
                                    fill="currentColor"/>
                            </svg>
                            :
                            notification.status === 'info'
                                ?
                                <svg style={{color: "white"}}
                                     viewBox="0 0 24 24" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M12 10.5199C11.59 10.5199 11.25 10.1799 11.25 9.76994V6.43994C11.25 6.02994 11.59 5.68994 12 5.68994C12.41 5.68994 12.75 6.02994 12.75 6.43994V9.76994C12.75 10.1899 12.41 10.5199 12 10.5199Z"
                                        fill="currentColor"/>
                                    <path
                                        d="M12.02 20.3502C9.43999 20.3502 6.86999 19.9402 4.41999 19.1202C3.50999 18.8202 2.81999 18.1702 2.51999 17.3502C2.21999 16.5302 2.31999 15.5902 2.80999 14.7702L4.07999 12.6502C4.35999 12.1802 4.60999 11.3002 4.60999 10.7502V8.65023C4.60999 4.56023 7.92999 1.24023 12.02 1.24023C16.11 1.24023 19.43 4.56023 19.43 8.65023V10.7502C19.43 11.2902 19.68 12.1802 19.96 12.6502L21.23 14.7702C21.7 15.5502 21.78 16.4802 21.47 17.3302C21.16 18.1802 20.48 18.8302 19.62 19.1202C17.17 19.9502 14.6 20.3502 12.02 20.3502ZM12.02 2.75023C8.75999 2.75023 6.10999 5.40023 6.10999 8.66023V10.7602C6.10999 11.5702 5.78999 12.7402 5.36999 13.4302L4.09999 15.5602C3.83999 15.9902 3.77999 16.4502 3.92999 16.8502C4.07999 17.2502 4.41999 17.5502 4.89999 17.7102C9.49999 19.2402 14.56 19.2402 19.16 17.7102C19.59 17.5702 19.92 17.2502 20.07 16.8302C20.23 16.4102 20.18 15.9502 19.95 15.5602L18.68 13.4402C18.26 12.7502 17.94 11.5802 17.94 10.7702V8.67023C17.93 5.40023 15.28 2.75023 12.02 2.75023Z"
                                        fill="currentColor"/>
                                    <path
                                        d="M12 22.8998C10.93 22.8998 9.87998 22.4598 9.11998 21.6998C8.35998 20.9398 7.91998 19.8898 7.91998 18.8198H9.41998C9.41998 19.4998 9.69998 20.1598 10.18 20.6398C10.66 21.1198 11.32 21.3998 12 21.3998C13.42 21.3998 14.58 20.2398 14.58 18.8198H16.08C16.08 21.0698 14.25 22.8998 12 22.8998Z"
                                        fill="currentColor"/>
                                </svg>
                                :
                                notification.status === "success" ?
                                    <svg style={{color: "white"}} width="24"
                                         height="24" viewBox="0 0 24 24"
                                         fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 12.3699L9.76 16.1199L18 7.87988"
                                              stroke="currentColor"
                                              stroke-width="1.5" stroke-linecap="round"
                                              stroke-linejoin="round"/>
                                    </svg>
                                    : <svg style={{color: "white"}} width="16"
                                           height="18" viewBox="0 0 16 18" fill="none"
                                           xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M12.53 0.599609H0V3.42961H12.53V5.76961H0V7.9696V8.59961V14.5696V15.9796V17.3996H5.18999H8.02V14.5696L10.87 17.3896V17.3996H15.36V14.5696H12.04L8.02 10.5796V10.5596H5.18999V14.5696H2.82999V8.59961H12.53H15.36V5.76961V3.42961V0.599609H12.53Z"
                                            fill="currentColor"/>
                                    </svg>
                        }
                        <span className="sr-only">Refresh icon</span>
                    </div>
                    <div className="text-content">
                        <h6 className={"text-sm md:text-base mb-0 font-semibold md:font-bold"}>{notification.title}</h6>
                        <p className={"text-xs font-normal md:text-sm"}>{notification.message}</p>
                    </div>
                    {/* Desktop-only close button */}
                    <IconButtonComponent type="button"
                                         onClick={(e: any) => {
                                             e.stopPropagation()
                                             dispatch({
                                                 type: "REMOVE_NOTIFICATION",
                                                 payload: {id: notification.id}
                                             })
                                         }}
                                         className="ms-auto md:!inline-flex -mx-1.5 -my-1.5 items-center justify-center shrink-0
                            rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100
                            hidden h-8 w-8 dark:text-gray-500 :text-white "
                                         data-dismiss-target="#toast-interactive"
                                         aria-label="Close">
                        <span className="sr-only">Close</span>
                        <svg className="w-3 h-3" aria-hidden="true"
                             xmlns="http://www.w3.org/2000/svg"
                             fill="currentColor"
                             viewBox="0 0 14 14">
                            <path stroke="currentColor" stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                        </svg>
                    </IconButtonComponent>
                </div>
            </motion.div>
        );
    };

    return (
        <ToastStyles>
            {notifications.length > 0 &&
                <div className={'notifications'}>
                    {status === "active" &&
                        <motion.div variants={collapsibleVariants} initial={"inactive"} animate={status}
                                    className={"collapsible"}>
                            <h5 className={"text-sm font-semibold"}>Notification</h5>
                            <ButtonComponent $size={"small"} onClick={() => {
                                notifications.forEach(d => {
                                    dispatch({type: "REMOVE_NOTIFICATION", payload: {id: d.id}})
                                })
                            }}
                                             size={"small"}>Clear</ButtonComponent>
                        </motion.div>}
                    <motion.div animate={status} initial={"inactive"} variants={wrapperVariants}
                                className={'relative notificationWrapper'}>
                        <AnimatePresence key={status} initial={true}>
                            {notifications.map((notification, index) => (
                                <DraggableToast
                                    key={notification.id}
                                    notification={notification}
                                    index={index}
                                />
                            ))}
                        </AnimatePresence>
                    </motion.div>
                </div>}
        </ToastStyles>
    );
};